/**
 * One question, asked before every spend: *are you sure?*
 *
 * Every purchase in the garage — a hull, a turret, an upgrade level, a top-tier
 * level, a hull part, a research node, a skip, a boost, a diamond pack — now
 * goes through here first, and each one shows *what it is buying*: see
 * `PurchaseArt` for the three kinds of picture and `PurchaseStats` for the stat
 * difference an upgrade makes. They are all irreversible and none of them can be
 * sold back, so the only protection against a misplaced click is the click
 * before it.
 *
 * ── Why one shared dialog and not a prompt per screen ────────────────────────
 * The spends live in five different components and there is no reason for five
 * different confirmations. A single pending request in module scope, rendered
 * once by `ConfirmPurchase.vue` near the root, means every purchase reads the
 * same and a sixth spend added tomorrow inherits it by calling one function.
 *
 * ── The promise is the whole API ─────────────────────────────────────────────
 * `askToBuy` resolves `true` or `false`, so a call site becomes two lines and
 * keeps its own control flow:
 *
 *     if (!(await askToBuy({ item: hull.name, price: hull.price }))) return;
 *     await garage.purchase(hull.id);
 *
 * The alternative — a store that remembers a pending *action* and performs it on
 * confirm — would mean every purchase path being expressible as data, which is
 * how a dialog ends up knowing about the shop.
 *
 * ── It is a courtesy, never a gate ───────────────────────────────────────────
 * Exactly like a disabled button. The server re-validates every purchase against
 * ownership, price, research and rank, and a client that skipped this dialog
 * entirely could not buy anything it was not already entitled to buy. Nothing
 * here may ever become the place a rule is enforced.
 *
 * ── One at a time ────────────────────────────────────────────────────────────
 * A second request while one is open resolves the *first* as declined rather
 * than queueing or discarding it. Queueing would pop a second dialog at somebody
 * who has already moved on; discarding would leave the first `await` pending
 * forever, and the code after it never runs — a purchase path silently wedged.
 * Declining is the only outcome that both settles the promise and does nothing.
 */
import { computed, ref } from 'vue';

import type { Framing } from '@/garage/model-thumbnail';
import type { StatDescriptor } from '@/garage/stat-display';

/**
 * The picture of what is being bought.
 *
 * A tagged union rather than a URL, because the three kinds of picture this game
 * already has are made in three completely different ways and only the call site
 * knows which it holds: a hull is a **rendered still** of the real GLB from the
 * shared cache, a part or a research node or a store pack is a **flat file on
 * disk** derived from its own id, and a title is **drawn in CSS** because a
 * nameplate is 14 device pixels tall and no raster survives that. Handing the
 * dialog a `src` for all three would mean inventing a raster for the two that do
 * not have one.
 *
 * Every kind degrades to no picture at all rather than to a broken one — see the
 * fallbacks in `ConfirmPurchase.vue`. That is the convention `model: null` on a
 * prop and a missing rank insignia already follow, and it is what keeps this
 * field safe to add to a dialog that must never block a spend it cannot
 * illustrate.
 *
 * `import type` on `Framing`: erased at compile time, so this does **not** put
 * `model-thumbnail` — and therefore Babylon — on the critical path. `ItemPreview`
 * still imports it dynamically for exactly that reason.
 */
export type PurchaseArt =
  /** A cached still of the real meshes: a hull, a turret, or an assembled tank. */
  | { kind: 'model'; hullId: string | null; turretId: string | null; framing: Framing }
  /** A flat image already in `public/assets`, derived from the item's own id. */
  | { kind: 'image'; src: string; contain?: boolean }
  /** A title, whose badge *is* the product and is drawn rather than fetched. */
  | { kind: 'title'; titleId: string };

/**
 * The stat difference a purchase would make, ready to render.
 *
 * Shaped as `StatList`'s own props so the dialog hands them straight over and a
 * stat reads identically here and on the card it was clicked from — same
 * descriptors, same formatting, same sign convention, same "lower is better"
 * table. Building a second comparison for the dialog is how the two would come
 * to disagree about whether a faster reload is an improvement.
 *
 * **`values` and `against` are both server-resolved figures.** For an upgrade
 * they are `nextStats` and `currentStats` off the same `GarageItem`; nothing here
 * derives a stat from a level. See `GarageItem.nextStats`.
 */
export interface PurchaseStats {
  descriptors: StatDescriptor[];
  values: Record<string, number>;
  against: Record<string, number>;
}

/** What the dialog says. Prices are formatted by the dialog, not by the caller. */
export interface PurchaseRequest {
  /** What is being bought, in the player's words: "Bulwark", "Composite Armour". */
  item: string;
  /**
   * Overrides the "Buy" in the headline question — "Research `{item}`?" for a
   * research node, which is not bought so much as started. The same reasoning
   * as `confirmLabel` on the button below, one line up: a caller that is not
   * really asking to *buy* something gets to say so in its own word rather than
   * inheriting one that describes a different kind of spend.
   */
  verb?: string;
  /** A quieter second line: "Tier 2 hull", "Upgrade to level 3". Optional. */
  detail?: string;
  price: number;
  currency: 'credits' | 'diamonds';
  /**
   * Renders instead of `price` and its unit, for a spend in something that is
   * not a game currency.
   *
   * The diamond packs are the case that needs it, and the reason is worth
   * stating: a pack is bought with **money** and pays out in diamonds, so the
   * obvious encoding — diamonds as the price — puts the number the player
   * *receives* in the dialog's largest text and the number they *pay* in the
   * small print. A confirmation whose headline figure is the reward is the shape
   * of a dark pattern, whatever the intent. The label is formatted by the caller
   * because only it knows the currency code.
   */
  priceLabel?: string;
  /** Overrides the confirm button's label, for a spend that is not a "buy". */
  confirmLabel?: string;
  /**
   * What the thing looks like. Optional, and absent is the old text-only dialog.
   *
   * Optional rather than required because two spends genuinely have nothing to
   * show — a custom diamond amount and the diamonds→credits conversion are both
   * a number becoming another number — and the rule this dialog is built on is
   * that a spend the screen cannot fully state goes through *unasked* rather than
   * being blocked. A missing picture must never be a reason not to offer a
   * purchase.
   */
  art?: PurchaseArt;
  /**
   * The stat difference, for a purchase that changes what the tank does.
   *
   * Set for an upgrade level and a top-tier level. Deliberately **not** set for
   * buying a hull or a turret outright: the card the click came from is already a
   * side-by-side comparison against what is fitted, and repeating it inside the
   * confirmation would put a wall of bars over a decision the player has just
   * made on better evidence. What an upgrade needs is the opposite — its card
   * shows one number and the dialog is the only place the *change* appears.
   */
  stats?: PurchaseStats;
}

interface Pending extends PurchaseRequest {
  settle: (accepted: boolean) => void;
}

const pending = ref<Pending | null>(null);

/** The request on screen, or `null`. Read by `ConfirmPurchase.vue`. */
export const purchaseRequest = computed<PurchaseRequest | null>(() =>
  pending.value
    ? {
        item: pending.value.item,
        verb: pending.value.verb,
        detail: pending.value.detail,
        price: pending.value.price,
        currency: pending.value.currency,
        priceLabel: pending.value.priceLabel,
        confirmLabel: pending.value.confirmLabel,
        art: pending.value.art,
        stats: pending.value.stats,
      }
    : null,
);

/**
 * Asks the player to confirm a spend. Resolves `true` if they accept.
 *
 * Never rejects: a caller writing `if (!(await askToBuy(...))) return;` must not
 * have to wrap it, because a confirmation that threw would abort the purchase
 * path in a way indistinguishable from a declined one but with a stack trace.
 */
export function askToBuy(request: PurchaseRequest): Promise<boolean> {
  // Settle whatever was already open as declined. See the note at the top.
  pending.value?.settle(false);
  return new Promise<boolean>((resolve) => {
    pending.value = {
      ...request,
      settle: (accepted) => {
        pending.value = null;
        resolve(accepted);
      },
    };
  });
}

/** Accepts the open request. No-op when nothing is open. */
export function acceptPurchase(): void {
  pending.value?.settle(true);
}

/** Declines the open request — the button, the backdrop and Escape all land here. */
export function declinePurchase(): void {
  pending.value?.settle(false);
}

/**
 * Test seam: abandons anything open without settling a real player's promise.
 *
 * Declines rather than drops, for the same reason `askToBuy` does — a suite that
 * left a promise pending would hang the next test that awaited it.
 */
export function resetPurchaseConfirm(): void {
  pending.value?.settle(false);
}
